/**
 * TimerDisplayV2 - Timer display component
 *
 * Features:
 * - Shows stage time (question time is now in QuestionCardV2)
 * - Countdown mode for Test Mode (shows remaining time)
 * - Warning states for time limits
 * - Animated styling
 */

import { memo } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectFormattedStageTime,
  selectIsCountdownMode,
  selectFormattedCountdownTime,
  selectCountdownWarningLevel
} from '../../../hooks/useChallenge'

// Clock icon
const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const TimerDisplayV2 = memo(({ compact = false }) => {
  const stageTime = useChallengeStoreV2(selectFormattedStageTime)
  const stageTimeSpent = useChallengeStoreV2((state) => state.stageTimeSpent)
  const currentStage = useChallengeStoreV2((state) => state.currentStage)

  // Countdown mode state
  const isCountdownMode = useChallengeStoreV2(selectIsCountdownMode)
  const countdownTime = useChallengeStoreV2(selectFormattedCountdownTime)
  const countdownWarningLevel = useChallengeStoreV2(selectCountdownWarningLevel)

  // Get time limit if any (for non-countdown mode)
  const timeLimit = currentStage?.configuration?.timeLimit
  const timeLimitSeconds = timeLimit ? timeLimit * 60 : null

  // Determine timer state
  let timerState = 'normal'
  if (isCountdownMode) {
    // Use countdown warning levels
    if (countdownWarningLevel === 'one') {
      timerState = 'danger'
    } else if (countdownWarningLevel === 'two') {
      timerState = 'urgent'
    } else if (countdownWarningLevel === 'three') {
      timerState = 'warning'
    }
  } else if (timeLimitSeconds) {
    const remaining = timeLimitSeconds - stageTimeSpent
    if (remaining <= 60) {
      timerState = 'danger'
    } else if (remaining <= 300) {
      timerState = 'warning'
    }
  }

  // Style classes based on state
  const stateClasses = {
    normal: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    urgent: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 animate-pulse'
  }

  // Display time and label
  const displayTime = isCountdownMode ? countdownTime : stageTime

  return (
    <div
      className={`
        flex items-center gap-1.5 rounded-full font-mono font-medium
        transition-all duration-300 ${stateClasses[timerState]}
        ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm gap-2'}
      `}
      title={
        isCountdownMode
          ? 'Time remaining'
          : `Time spent on this stage${timeLimitSeconds ? ` (Limit: ${timeLimit} min)` : ''}`
      }
    >
      <ClockIcon />
      <span className="tabular-nums">{displayTime}</span>
      {!isCountdownMode && timeLimitSeconds && !compact && (
        <span className="text-xs opacity-70">/ {timeLimit}m</span>
      )}
    </div>
  )
})

TimerDisplayV2.displayName = 'TimerDisplayV2'

export default TimerDisplayV2
